import { useState, useEffect } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import PhotoGrid from '../components/PhotoGrid';
import UploadButton from '../components/UploadButton';
import SearchBar from '../components/SearchBar';
import UploadModal from '../components/UploadModal';
import PhotoDetailModal from '../components/PhotoDetailModal';
// import ExportButton from '../components/ExportButton'; // Currently disabled due to API Gateway timeout

interface Tag {
  tag: string;
  type: 'person' | 'place' | 'thing';
  count: number;
}

export default function FeedPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTag, setSearchTag] = useState('');
  const [searchTagType, setSearchTagType] = useState<'person' | 'place' | 'thing' | 'all'>('all');
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | undefined>(undefined);
  const [showDirectPhotoModal, setShowDirectPhotoModal] = useState(false);

  // Sync from URL params when they change (e.g., clicking a tag link or sharing a URL)
  useEffect(() => {
    const tagFromUrl = searchParams.get('tag');
    const typeFromUrl = searchParams.get('type') as 'person' | 'place' | 'thing' | null;
    
    // Only sync if URL tag is DIFFERENT from current state
    // This allows URL navigation to work but prevents re-syncing during clear
    if (tagFromUrl && tagFromUrl !== searchTag) {
      setSearchTag(tagFromUrl);
      if (typeFromUrl && typeFromUrl !== searchTagType) {
        setSearchTagType(typeFromUrl);
      }
    } else if (!tagFromUrl && searchTag) {
      // URL has no tag but state does - clear the state (e.g., user hit back button)
      setSearchTag('');
      setSearchTagType('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams, not searchTag/searchTagType

  // Handle openUpload param
  useEffect(() => {
    const openUploadFromUrl = searchParams.get('openUpload');
    if (openUploadFromUrl === 'true') {
      setShowUploadModal(true);
      setSearchParams((params) => {
        params.delete('openUpload');
        return params;
      });
    }
  }, [searchParams, setSearchParams]);

  // Handle photoId param - fetch and open specific photo
  useEffect(() => {
    const photoIdFromUrl = searchParams.get('photoId');
    if (photoIdFromUrl && !selectedPhotoId) {
      setSelectedPhotoId(photoIdFromUrl);
      setShowDirectPhotoModal(true);
      // Clear the param from URL after setting it
      setSearchParams((params) => {
        params.delete('photoId');
        return params;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams, selectedPhotoId]);

  // Fetch specific photo details when selectedPhotoId is set
  const { data: selectedPhoto } = useQuery({
    queryKey: ['photo', selectedPhotoId],
    queryFn: () => api.getPhoto(selectedPhotoId!),
    enabled: !!selectedPhotoId && showDirectPhotoModal,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch tags for "Most Used Tags" section
  const { data: tagsData } = useQuery({
    queryKey: ['all-tags'],
    queryFn: () => api.listAllTags(),
  });

  const allTags: Tag[] = tagsData?.tags || [];
  const mostUsedTags = allTags
    .sort((a, b) => b.count - a.count)
    .slice(0, 25); // Show top 25 most used tags

  const handleTagClick = (tag: string, type: 'person' | 'place' | 'thing') => {
    setSearchTag(tag);
    setSearchTagType(type);
    setSearchParams({ tag, type });
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['photos', searchTag, searchTagType],
    queryFn: async ({ pageParam = undefined }) => {
      if (searchTag) {
        return api.searchPhotos({ 
          tag: searchTag,
          type: searchTagType !== 'all' ? searchTagType : undefined
        });
      }
      return api.listPhotos({ lastKey: pageParam });
    },
    getNextPageParam: (lastPage) => lastPage.lastKey,
    initialPageParam: undefined,
  });

  const photos = searchTag
    ? data?.pages[0]?.photos || []
    : data?.pages.flatMap((page) => page.photos) || [];

  // Debug: Log first photo to see structure
  if (photos.length > 0 && !photos[0].imageUrl && !photos[0].thumbnailUrls) {
    console.warn('Photo missing image URLs:', photos[0]);
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Photos</h1>
        
        <div className="flex items-center gap-2 sm:gap-3">
          {/* TODO: Re-enable ExportButton after implementing async export solution
              Currently disabled due to API Gateway 29-second timeout limit.
              Export process takes 40-80 seconds for large albums, causing timeouts.
              Need to implement: async job processing + email notification when ready */}
          {/* <ExportButton /> */}
          <UploadButton onClick={() => setShowUploadModal(true)} />
        </div>
      </div>

      {/* Most Used Tags */}
      {mostUsedTags.length > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl shadow-md p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">🔥</span>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Most Used Tags</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {mostUsedTags.map(({ tag, type, count }) => {
              const getTagColor = () => {
                switch (type) {
                  case 'person': return 'bg-blue-100 hover:bg-blue-200 border-blue-300 text-blue-900';
                  case 'place': return 'bg-green-100 hover:bg-green-200 border-green-300 text-green-900';
                  case 'thing': return 'bg-purple-100 hover:bg-purple-200 border-purple-300 text-purple-900';
                }
              };
              
              return (
                <button
                  key={`${tag}-${type}`}
                  onClick={() => handleTagClick(tag, type)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all hover:scale-105 hover:shadow-md ${getTagColor()}`}
                >
                  <span className="text-sm font-medium">
                    {type === 'person' && '👤'} {type === 'place' && '📍'} {type === 'thing' && '🎯'} {tag}
                  </span>
                  <span className="text-xs font-semibold bg-white/60 px-1.5 py-0.5 rounded">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sticky Search Bar */}
      <div style={{ position: 'sticky', top: '0', zIndex: 40 }} className="bg-white pb-4 pt-2 -mx-2 px-2 sm:-mx-4 sm:px-4 lg:-mx-4 lg:px-4 xl:-mx-6 xl:px-6 2xl:-mx-8 2xl:px-8 shadow-sm border-b border-gray-100">
        <SearchBar
          value={searchTag}
          onChange={setSearchTag}
          onClear={() => {
            setSearchTag('');
            setSearchTagType('all');
            setSearchParams({});
          }}
          tagType={searchTagType}
          onTagTypeChange={setSearchTagType}
        />
      </div>

      {/* Photos Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-primary-500"></div>
        </div>
      ) : isError ? (
        <div className="text-center py-16">
          <p className="text-red-500 text-lg mb-4">
            Error loading photos
          </p>
          <p className="text-gray-600 text-sm mb-4">
            {error?.message || 'Unknown error'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-500 text-lg">
            {searchTag
              ? `No photos found with tag "${searchTag}"`
              : 'No photos yet. Upload your first photo!'}
          </p>
        </div>
      ) : (
        <PhotoGrid
          photos={photos}
          onLoadMore={fetchNextPage}
          hasMore={hasNextPage}
          isLoadingMore={isFetchingNextPage}
          onPhotoDeleted={refetch}
          selectedPhotoId={selectedPhotoId}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={() => {
            setShowUploadModal(false);
            refetch();
          }}
        />
      )}

      {/* Direct Photo Detail Modal (when opened from link) */}
      {showDirectPhotoModal && selectedPhoto && (
        <PhotoDetailModal
          photo={selectedPhoto}
          onClose={() => {
            setShowDirectPhotoModal(false);
            setSelectedPhotoId(undefined);
            refetch();
          }}
        />
      )}
    </div>
  );
}

